import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { BaseDataService } from './base.data.service';
import { MessageService } from 'primeng';
import { IDelayFailureCategory } from '../interfaces/delay-failure/delay-failure-category';
import { ITaskDelayFailure } from '../interfaces/delay-failure/task-delay-failure';
import {
  IDelayFailureEmailResult,
  IDelayFailureTask,
  IDelayFailureTemplate,
} from '../interfaces/delay-failure/delay-failure-template';

@Injectable({ providedIn: 'root' })
export class DelayFailureService extends BaseDataService {
  url = '/api/delay-failures';
  entityTitle = 'Delay/Failures';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  public getDelayFailureCategories(active = false): Promise<IDelayFailureCategory[]> {
    let url = active == true ? `${this.url}/active-categories` : this.url;

    return this.handleRequest(
      this.http.get<IDelayFailureCategory[]>(url),
      this.handlersFactory.getDefault(),
    );
  }

  public getTaskDelayFailures(taskId): Promise<ITaskDelayFailure[]> {
    return this.handleRequest(
      this.http.get<ITaskDelayFailure[]>(`${this.url}/task-delay-failures/${taskId}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getJobDelayFailures(jobId): Promise<ITaskDelayFailure[]> {
    return this.handleRequest(
      this.http.get<ITaskDelayFailure[]>(`${this.url}/job-delay-failures/${jobId}`),
      this.handlersFactory.getDefault(),
    );
  }

  public addDelayFailure(delayFailure: IDelayFailureCategory): Promise<IDelayFailureCategory> {
    return this.handleRequest(
      this.http.post<IDelayFailureCategory>(`${this.url}`, delayFailure),
      this.handlersFactory.getDefault(),
    );
  }

  public updateDelayFailure(delayFailure: IDelayFailureCategory): Promise<IDelayFailureCategory> {
    return this.handleRequest(
      this.http.put<IDelayFailureCategory>(`${this.url}`, delayFailure),
      this.handlersFactory.getDefault(),
    );
  }

  public delayFailureActivation(id: number): Promise<IDelayFailureCategory> {
    return this.handleRequest(
      this.http.get<IDelayFailureCategory>(`${this.url}/activation/${id}`),
      this.handlersFactory.getDefault(),
    );
  }

  public addTaskDelayFailure(dto: ITaskDelayFailure) {
    return this.handleRequest(
      this.http.post<IDelayFailureCategory>(`${this.url}/task-delay-failure`, dto),
      this.handlersFactory.getDefault(),
    );
  }

  public getDelayFailureTemplates() {
    return this.handleRequest(
      this.http.get<IDelayFailureTemplate[]>(`${this.url}/delay-failures-template`),
      this.handlersFactory.getDefault(),
    );
  }

  public getDelayFailureEmails(taskId: number) {
    return this.handleRequest(
      this.http.get<IDelayFailureEmailResult>(`${this.url}/email-list/${taskId}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getDelayFailureTask(taskId: number) {
    return this.handleRequest(
      this.http.get<IDelayFailureTask>(`${this.url}/task/${taskId}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getRenderedDelayFailureTemplate(taskId: number, templateId: number) {
    return this.handleRequest(
      this.http.get<IDelayFailureTemplate>(
        `${this.url}/${taskId}/getRenderedDelayFailureTemplate/${templateId}`,
      ),
      this.handlersFactory.getDefault(),
    );
  }

  public deleteDelayFailureTemplate(id: number) {
    return this.handleRequest(
      this.http.delete<any>(`${this.url}/delete-delay-failure-template/${id}`),
      this.handlersFactory.getDefault(),
    );
  }

  public patchDelayFailureTemplate(id, patch: any) {
    return this.http
      .patch<any>(`${this.url}` + '/patch-delay-failure-template/' + id, patch)
      .toPromise()
      .catch(this.handleError);
  }

  createDelayFailureTemplate(dto) {
    return this.http
      .post<any>(`${this.url}` + '/create-delay-failure-template', dto)
      .toPromise()
      .catch(this.handleError);
  }
}
